import {
  type DefaultRole,
  PasswordRequester,
  type UserAttributes,
  type UserPreferences,
  type UserPreferencesToSave,
  type UserPreferencesWithoutStates,
  UserRequester,
} from '@cobliteam/api-lib';

import GraphQLUserRequester from '@cobliteam/api-lib/src/dashboard/user/GraphQLUserRequester';
import { type UserData, UserRole, type UUID } from '@cobliteam/common-types';
import { useGlobalFilterStore } from '@cobliteam/hooks';
import { Environment } from '@cobliteam/react-environments';
import type { AppState } from '@cobliteam/store';
import Alert from '@cobliteam/ui/components/Alert';
import { type UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import invariant from 'tiny-invariant';

import useLocalStorage from '../../useLocalStorage';
import useSessionUserStore from './store/useSessionUserStore';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getUserRequester = () => new UserRequester({ host: env.herbieApiHost });
const getUserRequesterGraphQl = () => new GraphQLUserRequester();
const getPasswordRequester = () => new PasswordRequester({ host: env.herbieApiHost });

export const QUERY_KEY = 'USER';
export const QUERY_KEY_USER_DATA = 'USER_DATA';
export const QUERY_KEY_USER_PREFERENCES = 'USER_PREFERENCES';

interface UserPreferencesCreateVariables {
  userId: UUID;
  preferencesToSave: UserPreferencesToSave;
}

interface UserPreferencesUpdateVariables {
  userId: UUID;
  preferencesToUpdate: UserPreferences;
}

interface UserPreferencesUpdateContext {
  previousPreferences?: UserPreferences;
}

interface UserPermissionsUpdateVariables {
  userId: UUID;
  role: UserRole;
  groupsRestriction: string[];
  customPermissions: string[];
}

interface PasswordUpdateVariables {
  email: string;
  oldPassword: string;
  newPassword: string;
}

const useUser = () => {
  const queryClient = useQueryClient();
  const session = useSelector((state: AppState) => state.login.session);
  const user = session?.user;
  const hasRootGroupAccess = user?.groupsRestriction?.length === 0 || (user?.groupsRestriction || []).includes('0/');
  const hasMaximumGroupsRestriction = (user?.groupsRestriction?.length ?? 0) >= 40;
  const hasGroupsRestriction: boolean = user?.groupsRestriction?.length !== undefined && user?.groupsRestriction?.length > 0;
  const isSafetyCoacher = user?.role === UserRole.SAFETY_COACHER;

  const [, setUserAttributes] = useLocalStorage<UserAttributes>('userAttributes');
  const [, setUserPreferencesWithoutStates] = useLocalStorage<UserPreferencesWithoutStates | undefined>('userPreferences');
  const setVehicleGroupIdsState = useGlobalFilterStore((state) => state.setVehicleGroupIds);

  const { hasNoValidGroups, setHasNoValidGroups, hasOnlyGroupsWithNoVehicles, setHasOnlyGroupsWithNoVehicles } = useSessionUserStore();

  const getSession = () => {
    invariant(session, 'Session is not defined');
    return session;
  };

  const getUser = () => {
    invariant(user, 'User is not defined');
    return user;
  };

  const userPreferencesWithoutStates = (userPreferences?: UserPreferences) => {
    if (!userPreferences) return undefined;

    return {
      ...userPreferences,
      useVehiclesCluster: userPreferences.useVehiclesCluster ?? false,
    };
  };

  const useGetUserData = (_userId?: UUID, enabled = true): UseQueryResult<UserData, unknown> => {
    return useQuery<UserData>({
      queryKey: [QUERY_KEY, QUERY_KEY_USER_DATA],
      queryFn: () =>
        getUserRequesterGraphQl()
          .getUserData({ session: getSession() })
          .then((userData: any) => {
            setUserPreferencesWithoutStates(userPreferencesWithoutStates(userData?.userPreferences));
            setVehicleGroupIdsState(userData?.userPreferences?.vehicleGroupIds || []);
            setUserAttributes(userData?.userAttributes);
            const currentFleet = userData.userFleets?.find((x: any) => x.id === session?.fleet.id);
            setHasNoValidGroups(currentFleet?.hasNoValidGroups ?? false);
            setHasOnlyGroupsWithNoVehicles(currentFleet?.hasOnlyGroupsWithNoVehicles ?? false);
            return userData;
          }),
      enabled,
      staleTime: 1000 * 60 * 5, // 5 minutes,
    });
  };

  const getDefaultRoles = (enabled = true) => {
    return useQuery<DefaultRole[]>({
      queryKey: [QUERY_KEY, 'DEFAULT_ROLES'],
      queryFn: () => getUserRequester().getDefaultRoles(),
      enabled,
      staleTime: Number.POSITIVE_INFINITY,
    });
  };

  const getPreferences = (userId?: UUID, enabled = true) => {
    const userIdLogged = enabled ? userId || getUser().id : '';

    return useQuery<UserPreferences | undefined>({
      queryKey: [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userIdLogged],
      queryFn: () => getUserRequester().getUserPreferences(userIdLogged),
      enabled,
    });
  };

  const canEditAllGroups = (groupPaths: string[]) => {
    const user = getUser();

    if (groupPaths.length === 0) {
      // only users with full access can edit any group
      return user.groupsRestriction.length === 0;
    }

    //user can edit all groups when:
    return (
      user.groupsRestriction.length === 0 || //has full access
      groupPaths.every((groupPath) => {
        //all given groups start with some of user's groups restriction
        return user.groupsRestriction.some((userGroup) => groupPath.startsWith(userGroup));
      })
    );
  };

  const updatePermissions = useMutation<void, unknown, UserPermissionsUpdateVariables>({
    mutationFn: ({ userId, role, groupsRestriction, customPermissions }) => getUserRequester().saveUserPermissions(userId, role, groupsRestriction, customPermissions),
  });

  const updatePassword = useMutation<void, unknown, PasswordUpdateVariables>({
    mutationFn: ({ email, oldPassword, newPassword }) => getPasswordRequester().changePassword(email, oldPassword, newPassword),
  });

  const createPreferences = useMutation<UUID, unknown, UserPreferencesCreateVariables>({
    mutationFn: (data) => getUserRequester().saveUserPreferences(data.userId, data.preferencesToSave),
    onMutate: async (data) => {
      const { userId } = data;

      queryClient.cancelQueries({
        queryKey: [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId],
      });

      const previousTodos = queryClient.getQueryData([QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId]);

      const createdUserPreferences: UserPreferences = {
        id: userId,
        vehicleGroupIds: data.preferencesToSave.vehicleGroupIds,
        useVehiclesCluster: data.preferencesToSave.useVehiclesCluster,
      };

      queryClient.setQueryData([QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId], createdUserPreferences);

      return { previousTodos };
    },
    onError: (_err, data, context: any) => {
      const { userId } = data;
      queryClient.setQueryData([QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId], context?.previousTodos);
      Alert.error('Erro ao salvar preferências. Tente novamente mais tarde.');
    },
    onSettled: (_newTodo, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, QUERY_KEY_USER_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, variables.userId],
      });
    },
  });

  const updatePreferences = useMutation<void, unknown, UserPreferencesUpdateVariables, UserPreferencesUpdateContext>({
    mutationFn: (variables) => getUserRequester().updateUserPreferences(variables.userId, variables.preferencesToUpdate),
    onMutate: (variables) => {
      const { userId } = variables;

      const previousPreferences: UserPreferences | undefined = queryClient.getQueryData([QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId]);

      // update userData
      const userData: any = queryClient.getQueryData([QUERY_KEY, QUERY_KEY_USER_DATA]);
      queryClient.setQueryData([QUERY_KEY, QUERY_KEY_USER_DATA], {
        ...userData,
        userPreferences: variables.preferencesToUpdate,
      });

      // update preferences
      queryClient.cancelQueries({
        queryKey: [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId],
      });
      queryClient.setQueryData([QUERY_KEY, QUERY_KEY_USER_PREFERENCES, userId], variables.preferencesToUpdate);

      return { previousPreferences };
    },
    onError: (_, variables, context) => {
      if (context?.previousPreferences) {
        queryClient.setQueryData([QUERY_KEY, QUERY_KEY_USER_PREFERENCES, variables.userId], context.previousPreferences);
      } else {
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY, QUERY_KEY_USER_PREFERENCES, variables.userId],
        });
      }
      Alert.error('Erro ao salvar preferências. Tente novamente mais tarde.');
    },
  });

  return {
    QUERY_KEY_USER_DATA,
    QUERY_KEY,
    user,
    /**
     * EN: Has root group access.
     *
     * PT-BR: Tem acesso ao grupo raiz.
     */
    hasRootGroupAccess,
    /**
     * EN: Has "no-valid-groups" in that user groupsRestriction, which means the only group he was, was deleted.
     *
     * PT-BR: Tem "no-valid-groups" na restrição de grupos, o que significa que o único grupo que ele estava, foi deletado.
     */
    hasNoValidGroups,
    /**
     * EN: Has 40 different groups restriction.
     *
     * PT-BR: Tem 40 restrições de grupos diferentes.
     */
    hasMaximumGroupsRestriction,
    /**
     * EN: Has groups restriction.
     *
     * PT-BR: Tem restrição de grupos.
     */
    hasGroupsRestriction,
    /**
     * EN: Has only groups with no vehicles.
     *
     * PT-BR: Tem apenas grupos sem veículos.
     */
    canEditAllGroups,

    /**
     * EN: No valid groups or no vehicles in any group
     *
     * PT-BR: Não tem grupos válidos ou não tem veículos em nenhum grupo.
     *
     * ````
     * ["no-valid-groups"] or ["group-no-vehicles"]
     * hasNoValidGroups = user has no valid groups
     * hasOnlyGroupsWithNoVehicles = user has groups but no exist vehicles in any of them
     * ````
     */
    noValidGroupOrVehicles: hasNoValidGroups || hasOnlyGroupsWithNoVehicles,
    /**
     * EN: Has only groups with no vehicles.
     *
     * PT-BR: Tem grupos porém não tem veículos.
     */
    hasOnlyGroupsWithNoVehicles,
    session,
    getUser,
    useGetUserData,
    getSession,
    getPreferences,
    getDefaultRoles,
    updatePassword,
    createPreferences,
    updatePreferences,
    updatePermissions,
    isSafetyCoacher,
  };
};

export default useUser;
